import {
  Analytics,
  getAnalytics,
  isSupported,
  setAnalyticsCollectionEnabled,
  setConsent,
} from "firebase/analytics";
import app, { FIREBASE_MEASUREMENT_ID } from "./firebase";

const CLARITY_PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID?.trim() || "";
// Google Analytics runs through Firebase Analytics, which always reports to the
// measurementId in the Firebase config.
const GA_MEASUREMENT_ID = FIREBASE_MEASUREMENT_ID;
const CLARITY_SCRIPT_ID = "clarity-script";
const TRACKING_COOKIE_PATTERN = /^(_ga|_gid|_gat|_clck|_clsk)/;

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };
const setGaDisabled = (disabled: boolean) => {
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = disabled;
};

let analytics: Analytics | null = null;
let gaLoading: Promise<Analytics | null> | null = null;
let clarityLoaded = false;

export const getAnalyticsInstance = () => analytics;

export const loadClarity = () => {
  if (!CLARITY_PROJECT_ID) return;
  if (!window.clarity) {
    const queue: ClarityFn = (...args: unknown[]) => {
      (queue.q = queue.q || []).push(args);
    };
    window.clarity = queue;
  }
  window.clarity("consentv2", { ad_Storage: "denied", analytics_Storage: "granted" });
  clarityLoaded = true;

  if (document.getElementById(CLARITY_SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = CLARITY_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
  document.head.appendChild(script);
};

export const loadGoogleAnalytics = async () => {
  setGaDisabled(false);
  setConsent({ analytics_storage: "granted", ad_storage: "denied" });

  if (analytics) {
    setAnalyticsCollectionEnabled(analytics, true);
    return analytics;
  }
  if (!gaLoading) {
    gaLoading = isSupported()
      .then((supported) => {
        analytics = supported ? getAnalytics(app) : null;
        return analytics;
      })
      .catch(() => null);
  }
  return gaLoading;
};

const clearTrackingCookies = () => {
  const host = window.location.hostname;
  const domains = ["", host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];

  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!TRACKING_COOKIE_PATTERN.test(name)) return;
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
        domain ? `; domain=${domain}` : ""
      }`;
    });
  });
};

// Stops further collection. Scripts that already ran stay in memory until the next page load.
export const disableAnalytics = () => {
  setGaDisabled(true);
  if (analytics) {
    setConsent({ analytics_storage: "denied", ad_storage: "denied" });
    setAnalyticsCollectionEnabled(analytics, false);
  }
  if (clarityLoaded && window.clarity) {
    window.clarity("consentv2", { ad_Storage: "denied", analytics_Storage: "denied" });
    // consentv2 "denied" only switches Clarity to cookieless mode; this erases its cookies and stops tracking.
    window.clarity("consent", false);
    clarityLoaded = false;
  }
  clearTrackingCookies();
};

export const enableAnalytics = () => {
  loadClarity();
  loadGoogleAnalytics().catch((error) => console.error("Failed to load Google Analytics:", error));
};
